/** @jsxImportSource @emotion/react */
import { Icon } from '@/components/icons';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getBannerById,
  resetMessages,
  updateBannerSlider,
  watchBannerDetail,
  watchBannerMessage
} from '@/redux/slices/banner.slice';
import { PageURLs } from '@/utils/navigate';
import { css } from '@emotion/react';
import {
  Typography,
  Button as ButtonAnt,
  Form,
  Input,
  Upload,
  Switch,
  Row,
  Col,
  UploadFile,
  FormProps
} from 'antd';
import ImgCrop from 'antd-img-crop';
import { UploadOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { setMessages } from '@/redux/slices/app.slice';
import { Button } from '../button';

type FieldType = {
  id: number;
  platFromId: number;
  name: string;
  imageUrl: [UploadFile] | null;
  redirectUrl: string;
  isActive: boolean;
};

export const BannerSliderEdit = () => {
  const [form] = Form.useForm();
  const { push, bannerId } = useQuery();
  const dispatch = useAppDispatch();
  const bannerDetail = useAppSelector(watchBannerDetail);
  const messageInfo = useAppSelector(watchBannerMessage);
  const [fileList, setFileList] = useState<any[]>([]);
  const [isNewImageUploaded, setIsNewImageUploaded] = useState(false);

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofHomeBannerSlider());
  }, [push]);

  const beforeUpload = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setFileList([{ url: reader.result }]);
    };

    return false;
  };

  const onFinish: FormProps<FieldType>['onFinish'] = (values) => {
    console.log({ fileList });
    let body: any = {
      id: Number(bannerId),
      id_platform: bannerDetail?.platFromId.toString(),
      is_active: values.isActive ? 'TRUE' : 'FALSE',
      redirect_url: values.redirectUrl,
      name: values.name
    };
    if (fileList.length > 0 && fileList[0].url && isNewImageUploaded) {
      body = {
        ...body,
        thumbnail: fileList[0].url as string
      };
    }

    dispatch(updateBannerSlider(body));
  };

  const handleRemove = () => {
    setFileList([]);
    setIsNewImageUploaded(false);
    form.setFieldValue('imageUrl', undefined);
  };

  useEffect(() => {
    if (!bannerId) {
      handleGoBack();
    } else {
      dispatch(getBannerById(bannerId));
    }
  }, [bannerId, dispatch, handleGoBack]);

  useEffect(() => {
    if (bannerDetail) {
      const image: UploadFile = {
        uid: bannerDetail.imageUrl!,
        name: bannerDetail.imageUrl!,
        status: 'done',
        url: `${bannerDetail.imageUrl!}`
      };
      setFileList([image]);
      form.setFieldsValue({
        name: bannerDetail.name,
        imageUrl: [image],
        redirectUrl: bannerDetail.redirectUrl,
        isActive: bannerDetail.isActive
      });
    }
  }, [bannerDetail, form]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo, form]);
  return (
    <div css={rootStyles}>
      <div css={boxStyles}>
        <Typography.Title css={titleStyles} level={5}>
          <ButtonAnt
            css={goBackBtnStyles}
            onClick={handleGoBack}
            type='text'
            icon={<Icon type='arrowLeft' width={20} height={20} />}
          />
          Chỉnh sửa banner
        </Typography.Title>
      </div>
      <div css={boxStyles}>
        <p css={subTitleStyles}>{bannerDetail?.option.name}</p>
      </div>
      <div css={boxStyles}>
        <Form
          form={form}
          name='create-banner'
          css={formStyles}
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete='off'
          className='ant-form-custom'
        >
          <Form.Item<CreateBannerProps>
            label='Tên banner'
            name='name'
            rules={[{ required: true, message: 'Vui lòng nhập tên Banner' }]}
          >
            <Input
              css={inputStyles}
              size='large'
              placeholder='Nhập tên Banner'
            />
          </Form.Item>

          <Form.Item<CreateBannerProps>
            label='Hình ảnh'
            name='imageUrl'
            rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList || [];
            }}
            valuePropName='fileList'
          >
            <ImgCrop
              aspect={2.86 / 1}
              onModalOk={(value) => {
                form.setFieldValue('imageUrl', value);
                form.validateFields(['imageUrl']);
              }}
              rotationSlider
              modalTitle='Cắt ảnh'
              modalOk='Xác nhận'
              modalCancel='Hủy'
              beforeCrop={(file) => file}
            >
              <Upload
                fileList={fileList}
                maxCount={1}
                beforeUpload={beforeUpload}
                listType='picture'
                accept='.png, .jpeg, .jpg'
                onRemove={handleRemove}
                onChange={() => setIsNewImageUploaded(true)}
              >
                <Button
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  icon={<UploadOutlined />}
                >
                  Tải lên hình ảnh
                </Button>
              </Upload>
            </ImgCrop>
          </Form.Item>

          <Form.Item<CreateBannerProps> label='URL' name='redirectUrl'>
            <Input
              css={inputStyles}
              size='large'
              placeholder='Nhập URL dẫn đến'
            />
          </Form.Item>

          <Form.Item<CreateBannerProps> label='Hiển thị' name='isActive'>
            <Switch
              checkedChildren='On'
              unCheckedChildren='Off'
              defaultChecked
            />
          </Form.Item>

          <Row css={formBottomStyles} justify='end' gutter={[20, 0]}>
            <Col>
              <Button
                onClick={handleGoBack}
                css={actionBtnStyles}
                shape='round'
              >
                Huỷ bỏ
              </Button>
            </Col>
            <Col>
              <Button
                // loading={loading}
                css={actionBtnStyles}
                htmlType='submit'
                type='primary'
                shape='round'
              >
                Lưu
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const boxStyles = css`
  padding: 1.4rem 2rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:not(&:first-of-type) {
    padding: 1.6rem 3.2rem;
  }
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;

const subTitleStyles = css`
  color: rgba(24, 144, 255, 1);
  font-weight: 400;
`;

const formStyles = css`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  .ant-form-item {
    .ant-form-item-label {
      width: 12rem;
      text-align: left;
      font-weight: 500;
      line-height: 5rem;
    }
    .ant-form-item-row {
      align-items: center;
    }
  }
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;

const inputStyles = css`
  height: 5rem;
`;

const formBottomStyles = css`
  margin-top: 2rem;
`;
